import {
  EFeatureFlag,
  type IProductList,
  type IProperty,
  type IPropertyFilter,
} from '@mahawi/eshop-common/dist/src/types';

export const PRODUCTS_PER_PAGE = 20; // category-types/PRODUCTS_PER_PAGE

export const STOCK_FILTER_DEFAULT = 'all' as const;
export const STOCK_FILTER_IN_STOCK = 'inStock' as const;
export const STOCK_FILTER_LOW_STOCK = 'lowStock' as const;
export const STOCK_FILTER_OUT_OF_STOCK = 'outOfStock' as const;

export const STOCK_FILTER_OPTIONS = [
  STOCK_FILTER_DEFAULT,
  STOCK_FILTER_IN_STOCK,
  STOCK_FILTER_LOW_STOCK,
  STOCK_FILTER_OUT_OF_STOCK,
] as const;

export type EStockFilter = (typeof STOCK_FILTER_OPTIONS)[number];

export const STOCK_FILTER_LOW_STOCK_THRESHOLD = 5;

export const PUBLIC_FEATURE_FLAGS: EFeatureFlag[] = [
  EFeatureFlag.FLAG_NEW,
  EFeatureFlag.FLAG_TIP,
];

export interface ILoadAllResponse {
  type: string;
  payload: {
    products: IProductList[];
  };
}

export type ILoadAllCallback = (arg0: ILoadAllResponse) => void;

export interface ILoadFeaturedResponse {
  type: string;
  payload: {
    products: IProductList[];
  };
}

export type ILoadFeaturedCallback = (arg0: ILoadFeaturedResponse) => void;

export interface ILoad {
  payload: {
    category: {
      uuid: string;
    };
    page: number;
    stock?: EStockFilter;
    featureFlags?: EFeatureFlag[];
  };
}

export interface ILoadResponse {
  type: string;
  payload: {
    products: IProductList[];
    pages: number;
    page: number;
  };
}

export type ILoadCallback = (arg0: ILoadResponse) => void;

export interface ILoadByBrand {
  payload: {
    category: {
      uuid: string;
    };
    brand: {
      uuid: string;
    };
    page: number;
    stock?: EStockFilter;
    featureFlags?: EFeatureFlag[];
  };
}

export interface ILoadByBrandResponse {
  type: string;
  payload: {
    products: IProductList[];
    pages: number;
    page: number;
  };
}

export type ILoadByBrandCallback = (arg0: ILoadByBrandResponse) => void;

export interface ISetActiveCategory {
  payload: {
    category: {
      uuid: string;
    };
  };
}

export interface ILoadCategoryProperties {
  payload: {
    category: {
      uuid: string;
      propertyCodes: string[];
    };
  };
}

export interface ILoadCategoryPropertiesResponse {
  type: string;
  payload: {
    properties: IProperty[];
  };
}

export type ILoadCategoryPropertiesCallback = (
  arg0: ILoadCategoryPropertiesResponse,
) => void;

export interface ILoadByProperties {
  payload: {
    category: {
      uuid: string;
    };
    filters: IPropertyFilter[];
    page: number;
    brand?: {
      uuid: string;
    };
    stock?: EStockFilter;
    featureFlags?: EFeatureFlag[];
  };
}

export interface ILoadByPropertiesResponse {
  type: string;
  payload: {
    products: IProductList[];
    pages: number;
    page: number;
    total: number;
  };
}

export type ILoadByPropertiesCallback = (
  arg0: ILoadByPropertiesResponse,
) => void;

export interface ILoadFilteredTotal {
  payload: {
    category: {
      uuid: string;
    };
    filters: IPropertyFilter[];
    brand?: {
      uuid: string;
    };
    stock?: EStockFilter;
  };
}

export interface ILoadFilteredTotalResponse {
  type: string;
  payload: {
    total: number;
  };
}

export type ILoadFilteredTotalCallback = (
  arg0: ILoadFilteredTotalResponse,
) => void;

export interface ISetPropertyFilter {
  payload: IPropertyFilter;
}

export interface IRemovePropertyFilter {
  payload: {
    code: string;
  };
}

export interface ILoadAvailableValues {
  payload: {
    category: {
      uuid: string;
    };
    filters: IPropertyFilter[];
    propertyCodes: string[];
    brand?: {
      uuid: string;
    };
    stock?: EStockFilter;
  };
}

export interface ILoadAvailableValuesResponse {
  type: string;
  payload: {
    availableValues: Record<string, string[]>;
  };
}

export type ILoadAvailableValuesCallback = (
  arg0: ILoadAvailableValuesResponse,
) => void;

export interface ILoadCategoryAvailability {
  payload: {
    category: {
      uuid: string;
    };
    filters: IPropertyFilter[];
    brand?: {
      uuid: string;
    };
  };
}

export interface ILoadCategoryAvailabilityResponse {
  type: string;
  payload: {
    availableStockOptions: EStockFilter[];
    availableFeatureFlags: EFeatureFlag[];
  };
}

export type ILoadCategoryAvailabilityCallback = (
  arg0: ILoadCategoryAvailabilityResponse,
) => void;

export interface IProductsState {
  isLoading: boolean;
  category: {
    uuid: string;
  };
  products: IProductList[];
  pages: number;
  page: number;
  categoryProperties: IProperty[];
  propertyFilters: IPropertyFilter[];
  filteredTotal: number | null;
  availableValues: Record<string, string[]> | null;
  availableFeatureFlags: EFeatureFlag[] | null;
  availableStockOptions: EStockFilter[] | null;
  featured: IProductList[];
}
